/**
 * <ResetRequestForm> — `/auth/forgot` password-reset request screen.
 *
 * Per UI-SPEC §5 + §6 + AUTH-09 (always-resolve enumeration defense):
 *   - Email field + submit button + back link.
 *   - `requestReset(...)` ALWAYS resolves void; the component's only
 *     post-submit work is flipping to the "check your email" confirmation
 *     state (same card, no route change).
 *   - The confirmation copy is IDENTICAL regardless of whether the email
 *     was registered — the UI must not leak enumeration.
 *
 * Back link takes users to `/login` via the app-supplied `onBack` callback
 * (apps wire to TanStack Router).
 *
 * A username (no `@`) has no inbox to send a link to: the form says to ask the
 * company administrator and makes no call.
 */
import { useState, type JSX } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { Alert, Button, Form, FormField, Input } from "@linkhr/design-system";
import { resetRequestSchema, type ResetRequestSchema } from "../schemas/authSchemas.js";
import { useAuth } from "../react/useAuth.js";

export interface ResetRequestFormProps {
  tenantSlug: string;
  apiBaseUrl: string;
  /**
   * Reset-link audience (CC-214) — `"mobile"` for Employee Web, `"admin"` for
   * HR Admin, `"accounting"` for Link Accounting. Routes the emailed link to
   * the right base path; omit to default to `/me` server-side.
   */
  audience?: "admin" | "mobile" | "accounting";
  /** Router callback: "Back to sign in" — typically navigates to `/login`. */
  onBack: () => void;
}

export function ResetRequestForm({
  tenantSlug,
  apiBaseUrl,
  audience,
  onBack,
}: ResetRequestFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const [sent, setSent] = useState(false);
  const [askAdmin, setAskAdmin] = useState(false);

  const methods = useForm<ResetRequestSchema>({
    resolver: zodResolver(resetRequestSchema),
    defaultValues: { identifier: "" },
    mode: "onSubmit",
  });

  const onSubmit = methods.handleSubmit(async (values) => {
    if (!values.identifier.includes("@")) {
      setAskAdmin(true);
      return;
    }
    setAskAdmin(false);
    // AUTH-09: always resolves. No try/catch needed — the action itself
    // swallows non-2xx responses per its contract.
    await auth.requestReset({
      email: values.identifier,
      tenantSlug,
      apiBaseUrl,
      ...(audience ? { audience } : {}),
    });
    setSent(true);
  });

  if (sent) {
    return (
      <div className="flex flex-col gap-md">
        <h1 className="text-heading text-default">{t("forgot.sent.heading")}</h1>
        <p className="text-body text-muted">{t("forgot.sent.body")}</p>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          {t("forgot.back")}
        </Button>
      </div>
    );
  }

  const isSubmitting = methods.formState.isSubmitting;

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit} aria-labelledby="forgot-heading">
        <h1 id="forgot-heading" className="text-heading text-default">
          {t("forgot.heading")}
        </h1>

        {askAdmin && <Alert variant="info" description={t("forgot.contact_admin")} />}

        <FormField<ResetRequestSchema, "identifier">
          name="identifier"
          label={t("forgot.email_label")}
          required
          render={(field) => (
            <Input
              id={field.id}
              name={field.name}
              type="text"
              bidi="ltr"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={field.ref}
              invalid={field.invalid}
              aria-describedby={field["aria-describedby"]}
              aria-invalid={field["aria-invalid"]}
              aria-required={field["aria-required"]}
            />
          )}
        />

        <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
          {t("forgot.cta")}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          {t("forgot.back")}
        </Button>
      </Form>
    </FormProvider>
  );
}
